import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ShieldCheck, Beaker, Check, Wrench, Play, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CADENCES = [
  { label: "Weekdays, 7:00am", cron: "0 7 * * 1-5" },
  { label: "Daily, 7:00am", cron: "0 7 * * *" },
  { label: "Every Friday, 4:30pm", cron: "30 16 * * 5" },
  { label: "Every Monday, 8:00am", cron: "0 8 * * 1" },
  { label: "Hourly (testing)", cron: "0 * * * *" },
];

const STEPS = [
  { key: "understand", label: "Understand the request" },
  { key: "build", label: "Build the skill" },
  { key: "sandbox", label: "Test in sandbox" },
  { key: "permissions", label: "Permissions" },
  { key: "install", label: "Install to your vault" },
];

const EXAMPLES = [
  "Summarize my week every Friday",
  "Connect to my CRM and log every meeting",
  "Flag investor emails",
  "Draft a thank-you note after every intro",
];

export default function BuildMode() {
  const navigate = useNavigate();
  const [request, setRequest] = useState("");
  const [skills, setSkills] = useState([]);
  const [activeStep, setActiveStep] = useState(-1); // -1 idle, 0..4 in flight, 5 awaiting approval (perm step)
  const [pendingSkill, setPendingSkill] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const load = () => api.get("/skills").then((r) => setSkills(r.data));
  useEffect(() => { load(); }, []);

  const patchSchedule = async (id, body) => {
    try {
      const r = await api.patch(`/skills/${id}/schedule`, body);
      setSkills((prev) => prev.map((s) => (s.id === id ? r.data : s)));
    } catch {
      toast.error("Couldn't update the schedule.");
    }
  };

  const runSkillNow = async (s) => {
    if (runningId) return;
    setRunningId(s.id);
    try {
      await api.post(`/skills/${s.id}/run`);
      toast.success("Briefing generated — see Briefings.", {
        action: { label: "Open", onClick: () => navigate("/briefings") },
      });
    } catch {
      toast.error("Couldn't run the skill.");
    } finally {
      setRunningId(null);
    }
  };

  const begin = async (text) => {
    if (busy || !text.trim()) return;
    setBusy(true);
    setPendingSkill(null);
    setActiveStep(0);

    // Step 0–1 animation
    await delay(900);
    setActiveStep(1);

    // Backend build (synthesize)
    let skill;
    try {
      const r = await api.post("/skills/build", { request: text });
      skill = r.data;
    } catch {
      toast.error("Couldn't build the skill.");
      setActiveStep(-1);
      setBusy(false);
      return;
    }

    await delay(1100);
    setActiveStep(2);
    await delay(1100);
    setActiveStep(3);
    setPendingSkill(skill);
    setBusy(false);
  };

  const approveInstall = async () => {
    if (!pendingSkill) return;
    setBusy(true);
    setActiveStep(4);
    try {
      await api.post(`/skills/${pendingSkill.id}/approve`);
      await delay(700);
      await load();
      toast.success("Installed to your vault.");
      setPendingSkill(null);
      setRequest("");
      setActiveStep(-1);
    } catch {
      toast.error("Couldn't install.");
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    setPendingSkill(null);
    setActiveStep(-1);
    setRequest("");
  };

  return (
    <>
      <div className="surface-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="surface-title">Build Mode</div>
          <span className="badge" style={{
            fontSize: 10, padding: "2px 8px", borderRadius: 999,
            background: "rgba(123,111,232,0.18)", color: "var(--ph-accent-purple)",
            border: "1px solid rgba(123,111,232,0.40)", letterSpacing: "0.08em",
          }}>NEW</span>
        </div>
        <span className="status-pill"><span className="dot" />Sandbox · least-privilege</span>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "28px clamp(14px, 4vw, 32px) 60px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }} className="fade-up">
          {/* Request */}
          <div className="panel" style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <Sparkles size={16} className="text-cobalt" />
              <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                Teach a new skill
              </div>
            </div>
            <h3 style={{ lineHeight: 1.35 }}>
              What should Andre learn to do?
            </h3>
            <textarea
              className="field"
              style={{ marginTop: 18, minHeight: 92 }}
              placeholder="Describe a routine, a workflow, or a habit you want to delegate…"
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              data-testid="build-input"
              disabled={busy || activeStep !== -1}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {EXAMPLES.map((ex, i) => (
                <button key={ex} className="chip" onClick={() => setRequest(ex)} disabled={busy || activeStep !== -1} data-testid={`build-example-${i}`}>
                  {ex}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                className="btn btn-primary"
                onClick={() => begin(request)}
                disabled={busy || !request.trim() || activeStep !== -1}
                data-testid="build-submit"
              >
                <Wrench size={14} /> Build it
              </button>
            </div>
          </div>

          {/* Steps */}
          {activeStep !== -1 && (
            <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 10 }} data-testid="build-steps">
              {STEPS.map((s, i) => {
                const state = i < activeStep ? "done" : i === activeStep ? "active" : "";
                return (
                  <div key={s.key} className={`build-step ${state}`} data-testid={`build-step-${i}`}>
                    <div className="step-num">{i < activeStep ? <Check size={13} /> : i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{s.label}</div>
                      {i === activeStep && pendingSkill && i === 3 && (
                        <div style={{ marginTop: 12 }} data-testid="permissions-panel">
                          <div className="text-soft" style={{ fontSize: 13.5, marginBottom: 10 }}>
                            <strong style={{ color: "var(--ink)" }}>{pendingSkill.name}</strong> would like the following scopes:
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {pendingSkill.scopes.map((sc) => (
                              <span key={sc} style={{
                                fontSize: 12, padding: "4px 10px", borderRadius: 999,
                                border: "1px solid var(--hairline)", color: "var(--ink-soft)",
                                background: "var(--panel-2)",
                              }} data-testid={`scope-${sc}`}>
                                <ShieldCheck size={11} style={{ display: "inline", marginRight: 6, color: "var(--good)" }} />
                                {sc}
                              </span>
                            ))}
                          </div>
                          {pendingSkill.schedule && (
                            <div className="text-dim" style={{ marginTop: 10, fontSize: 12.5 }}>
                              Schedule: {pendingSkill.schedule}
                            </div>
                          )}
                          <div className="text-dim" style={{ marginTop: 10, fontSize: 12 }}>
                            Sandbox-tested. Drafts-only. Bound by your Commandments.
                          </div>
                          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                            <button className="btn btn-ghost" onClick={cancelPending} data-testid="build-cancel">Cancel</button>
                            <button className="btn btn-primary" onClick={approveInstall} data-testid="build-approve">
                              Approve & Install
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Installed grid */}
          <div style={{ marginTop: 36 }}>
            <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12 }}>
              Installed skills
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }} data-testid="installed-skills">
              {skills.filter((s) => s.status === "installed").map((s) => (
                <div key={s.id} className="panel fade-up" data-testid={`skill-${s.id}`} style={{ padding: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Beaker size={14} className="text-cobalt" />
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                  </div>
                  <div className="text-soft" style={{ fontSize: 13, marginTop: 10, lineHeight: 1.55 }}>
                    {s.description}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
                    {(s.scopes || []).map((sc) => (
                      <span key={sc} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, border: "1px solid var(--hairline)", color: "var(--ink-dim)" }}>
                        {sc}
                      </span>
                    ))}
                  </div>

                  {/* Schedule controls */}
                  <div className="skill-sched" data-testid={`skill-sched-${s.id}`}>
                    <label className="skill-toggle">
                      <input
                        type="checkbox"
                        checked={!!s.enabled}
                        onChange={(e) => patchSchedule(s.id, { enabled: e.target.checked })}
                        data-testid={`skill-toggle-${s.id}`}
                      />
                      <span className="skill-toggle-track"><span className="skill-toggle-thumb" /></span>
                      <span style={{ fontSize: 12.5, color: s.enabled ? "var(--ink)" : "var(--ink-dim)" }}>
                        {s.enabled ? "Scheduled" : "Paused"}
                      </span>
                    </label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <Clock size={12} className="text-dim" />
                      <select
                        className="skill-cadence"
                        value={s.cron || ""}
                        onChange={(e) => {
                          const c = CADENCES.find((x) => x.cron === e.target.value);
                          patchSchedule(s.id, { cron: e.target.value, schedule: c ? c.label : s.schedule });
                        }}
                        data-testid={`skill-cadence-${s.id}`}
                      >
                        {!CADENCES.find((x) => x.cron === s.cron) && s.cron && (
                          <option value={s.cron}>{s.schedule || s.cron}</option>
                        )}
                        {CADENCES.map((c) => (
                          <option key={c.cron} value={c.cron}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="btn"
                      style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
                      onClick={() => runSkillNow(s)}
                      disabled={runningId === s.id}
                      data-testid={`skill-run-${s.id}`}
                    >
                      {runningId === s.id ? <Loader2 size={13} className="spin" /> : <Play size={13} />} Run now
                    </button>
                  </div>
                </div>
              ))}
              {skills.filter((s) => s.status === "installed").length === 0 && (
                <div className="text-dim">No skills installed yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));
